import { ReactElement, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { Mutation_Root, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { resolveDetailsRoot } from 'src/routing/routing-utils'
import { initialValuesFactory, MeasureForm, MeasureFormValues } from 'src/screens/shared/measure/MeasureForm'
import { queryMeasureById, updateMeasure } from 'src/screens/shared/measure/measureQueries'
import { usePermissionsForProject } from 'src/service/security/PermissionHook'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { MEASURE_CHAR } from 'src/shared/constants/constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useError } from 'src/shared/utils/hooks/page-loading-error-hook'
import { useIsMounted } from 'src/shared/utils/hooks/react-hooks'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Utils } from 'src/shared/utils/Utils'
import { useClient } from 'urql'

type originType = 'SAVE' | 'SAVE_AND_BACK'

interface ProjectMeasureEditPageProps {
  baseUrl: '/pf-kap' | '/pf-pgv'
}

export const ProjectMeasureEditPage = ({ baseUrl }: ProjectMeasureEditPageProps): ReactElement => {
  const isMounted = useIsMounted()
  const { projectId, measureId } = useParams()
  const { getMessage } = useMessageSource()
  const [initialValues, setInitialValues] = useState<MeasureFormValues | null>(null)
  const originRef = useRef<originType>('SAVE')
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const process = Utils.resolveProcessToLowerCase(baseUrl)
  const projectType = Utils.resolveProcess(baseUrl)
  const { canEdit, loading } = usePermissionsForProject(projectType, parseInt(projectId as string))
  const setError = useError()

  useEffect(() => {
    const initializeFormValues = async () => {
      if (measureId) {
        const { data } = await urqlClient
          .query<{ project_measure_by_pk: Query_Root['project_measure_by_pk'] }, { id: number }>(queryMeasureById, {
            id: parseInt(measureId),
          })
          .toPromise()

        if (data && data.project_measure_by_pk) {
          const measure = initialValuesFactory(data.project_measure_by_pk.project_base_id, data.project_measure_by_pk)
          setInitialValues(measure)
        } else {
          setError()
        }
      }
    }
    initializeFormValues()
  }, [measureId, projectId, urqlClient, setError])

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }
    document.getElementById('measure-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const onUpdateHandler = async (values: any) => {
    const goals = values.goalIds.map((goalId: number) => ({
      project_measure_id: values.measureId,
      project_goal_id: goalId,
    }))
    const updateObject = {
      id: values.measureId,
      version: values.version,
      name: values.name,
      description: HtmlSanitizer.sanitize(values.description),
      ...(values.startDate && { startDate: DateUtils.parseAndSerializeDate(values.startDate) }),
      ...(values.endDate && { endDate: DateUtils.parseAndSerializeDate(values.endDate) }),
      goalsToBeAdded: goals,
    }

    const { error, data } = await urqlClient
      .mutation<{
        update_project_measure: Mutation_Root['update_project_measure']
        delete_project_goal_measure: Mutation_Root['delete_project_goal_measure']
        insert_project_goal_measure: Mutation_Root['insert_project_goal_measure']
      }>(updateMeasure, updateObject)
      .toPromise()

    if (error || data?.update_project_measure?.affected_rows !== 1) {
      notificationService.operationFailed()
    } else if (isMounted()) {
      const newVersion = data?.update_project_measure.returning[0]?.version
      const newInitialValues = { ...values, version: newVersion }
      setInitialValues(newInitialValues)
      notificationService.changesSaved()
      if (originRef.current === 'SAVE_AND_BACK') {
        onBack()
      }
    }
  }

  const onBack = () => {
    const route = resolveDetailsRoot(baseUrl).nested.GoalsAndMeasures.params({ projectId })
    navigate(`${route}#${MEASURE_CHAR}${measureId}`, { state: { expandedEntityId: `${MEASURE_CHAR}${measureId}` } })
  }

  if (!loading && !canEdit) {
    return <NotAuthorized />
  }

  return (
    <ScreenLayout
      title={getMessage('label.measure.title.edit')}
      hasSecondLevelNavigation={false}
      onBack={onBack}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={onSubmit('SAVE')} />
        </>
      }
    >
      <PageLayout>
        <>
          {!loading && initialValues && (
            <>
              <HelpAndInstructions labelKey={`label.help.goals.and.measures.measures.${process}`} defaultExpansion />
              <MeasureForm initialValues={initialValues} onSave={onUpdateHandler} />
            </>
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
